import { Component, Element, Event, EventEmitter, h, Host, Listen, Method, Prop, State, Watch } from "@stencil/core";
import { applySchemeDelegate, ColorableComponent, setCssVariablesDelegate } from "../../common/colorable-component";
import {
  CarouselSchemeDefinition,
  CarouselSchemeName,
  ControlStyle,
  IndicatorPosition,
  IndicatorSize
} from "./constants/carousel-types";
import {
  CAROUSEL_SCHEME_CONFIG,
  CAROUSEL_SCHEME_MAP,
  CONTROL_STYLE_DEFAULT,
  INDICATORPOSITION_CENTER,
  INDICATORSIZE_LARGE,
  INDICATORSIZE_SMALL
} from "./constants/carousel-constants";
import pfx from "../../utils/style-class-prefix-helper";
import { detectSlotChanges } from "../../utils/slot-helper";
import { CarouselSchemeConfiguration } from "../../constants/colors";
import Splide from "@splidejs/splide";
import { ICONSIZE_NORMAL } from "../icon/constants/icon-constants";
import { KeyIds } from "../../constants/keys";
import { scrollToElement } from "../../utils/scroll-helper";
import {
  ContainerBreakpointComponent,
  initContainerBreakpointsDelegate
} from "../../common/container-breakpoint-component";
// import { InitLogging } from "../../decorators/init-logging";

/**
 * @slot [unnamed] - Holds any number of eon-ui-carousel-items
 */
@Component({
  tag: "eon-ui-carousel",
  styleUrl: "carousel.scss",
  assetsDirs: ["assets-carousel"],
  shadow: true
})
export class Carousel
  implements ColorableComponent<CarouselSchemeConfiguration<CarouselSchemeDefinition>>, ContainerBreakpointComponent {
  // @InitLogging() sentryTransaction;
  schemeConfig: CarouselSchemeConfiguration<CarouselSchemeDefinition> = CAROUSEL_SCHEME_CONFIG;
  schemeMap = CAROUSEL_SCHEME_MAP;

  @State() splide: any;
  splideConfig: any;
  splideContainer: any;
  splideTrack: any;

  viewportHeight: any;

  @State() slideItems: Element[];
  @State() activeItemIndex: number = 0;
  @State() slides: any;
  @State() activeSlideIndex: number = 0;
  @State() activeSlideHeight: number;
  @State() stickyControls: boolean;
  @State() stickyBottomControlPositionFixed: boolean = false;
  @State() stickyCenterControlPositionFixed: boolean = false;
  @State() stickyControlLeftPosition: any = 0;
  @State() stickyControlRightPosition: any = 0;
  @State() stickyControlBottomPosition: any = 0;
  @State() stickyOutsideControlTopPosition: any = 0;

  scrollTimeout: any;
  resizeTimeout: any;
  indicatorContainer: any;

  chevronLeftIcon: string = "chevron_small_left";
  chevronRightIcon: string = "chevron_small_right";

  containerBreakpoints: number[] = [943, 640];

  carouselState = {
    itemsCount: 0,
    slidesCount: 0,
    currentItemIndex: 0,
    currentSlideIndex: 0,
    firstVisibleItemIndex: 0,
    lastVisibleItemIndex: 0
  };

  @State() activeScheme: CarouselSchemeDefinition;

  @Element() hostElement: HTMLEonUiCarouselElement;

  /**
   * The color scheme of the carousel.
   */
  @Prop({ reflect: true }) scheme: CarouselSchemeName;

  /**
   * Display controls to switch to previous or next slide.
   * @default true
   */
  @Prop() controls: boolean = true;

  /**
   * Display controls in a dark theme for bright backgrounds.
   * @default false
   */
  @Prop() darkenControls: boolean = false;

  /**
   * Display indicators showing index of current slide.
   * @default true
   */
  @Prop({ attribute: "indicators" }) setIndicators: boolean = true;

  /**
   * Sets the size of indicators.
   * @default INDICATORSIZE_LARGE
   */
  @Prop() indicatorSize: IndicatorSize = INDICATORSIZE_LARGE;

  /**
   * Sets the position of indicators.
   * @default INDICATORPOSITION_CENTER
   */
  @Prop() indicatorPosition: IndicatorPosition = INDICATORPOSITION_CENTER;

  /**
   * Display indicators above slides and add transparent color scheme.
   * @default false
   */
  @Prop() indicatorsOnImage: boolean = false;

  /**
   * If set, slides will switch after the defined time in miliseconds.
   * Must be at least 1000.
   */
  @Prop() autoplay: number = 0;

  /**
   * If set, carousel auto play pauses when user is hovering the carousel.
   * @default true
   */
  @Prop({ attribute: "pause" }) pauseOnHover: boolean = true;

  /**
   * Defines a fixed height for all slides with overflow hidden.
   * @example "200px", "100px"
   */
  @Prop() slideHeight: string;

  /**
   * Raised after the active slide has changed. Returns the index of the slide tab.
   */
  @Event() slideDragged: EventEmitter<any>;

  /**
   * sameHeight- will apply the same height to all carousel items
   */
  @Prop() sameHeight = false;

  /**
   * loop - fixed start and end point of carousel contents
   * @example "true", "false"
   */
  @Prop() loop: boolean = true;

  /**
   * Set control position
   */
  @Prop({ reflect: true, mutable: true }) controlStyle: ControlStyle = CONTROL_STYLE_DEFAULT;

  /**
   * Sets the amount of items to be displayed on one slide.
   * @default 1
   */
  @Prop() itemsPerSlide: number = 1;

  /**
   * Sets the amount of items to be displayed on one slide on desktop breakpoint.
   */
  @Prop() itemsPerSlideDesktop: number;

  /**
   * Sets the amount of items to be displayed on one slide on tablet breakpoint.
   */
  @Prop() itemsPerSlideTablet: number;

  /**
   * Sets the amount of items to be displayed on one slide on mobile breakpoint.
   */
  @Prop() itemsPerSlideMobile: number;

  /**
   * Determines the number of items to move at once.
   * @default 1
   */
  @Prop() itemsPerMove: number = 1;

  /**
   * Determines the number of items to move at once on desktop breakpoint.
   */
  @Prop() itemsPerMoveDesktop: number;

  /**
   * Determines the number of items to move at once on tablet breakpoint.
   */
  @Prop() itemsPerMoveTablet: number;

  /**
   * Determines the number of items to move at once on mobile breakpoint.
   */
  @Prop() itemsPerMoveMobile: number;

  /**
   * Defines the width of the gap between each of the slide items and slides.
   */
  @Prop() gap: string;

  /**
   * Full width version of the carousel that allows visible overlap if the parent container comes with overflow: visible.
   * WARNING: To have this feature properly working overflow-x: hidden; has to be set for the body.
   */
  @Prop() fullWidth: boolean;

  /**
   * The Carousel will respect the width of each slide if the autoWidth option is true, which allows slides to have their own width.
   */
  @Prop() autoWidth: boolean;

  /**
   * Defines the focus of arrow keys.
   */
  @Prop() arrowFocus: boolean = true;

  /**
   * Returns the current carousel state.
   */
  @Method()
  async goToSlide(index) {
    this.goToSlideWithIndex(index);
  }

  /**
   * Returns the current carousel state.
   */
  @Method()
  async getCarouselState(): Promise<any> {
    return this.carouselState;
  }

  /**
   * Returns the index of the active slide.
   */
  @Method()
  async getActiveSlideIndex(): Promise<number> {
    return this.activeSlideIndex;
  }

  /**
   * Set index for new active slide. Carousel automatically focusses the new active slide.
   * @example "0", "2"
   */
  @Method()
  async setActiveSlideIndex(index: number) {
    this.goToPageWithIndex(index);
  }

  /**
   * Returns the index of the active item.
   */
  @Method()
  async getActiveItemIndex(): Promise<number> {
    return this.activeItemIndex;
  }

  /**
   * Set index for new active item. Carousel automatically focusses the new active item.
   * @example "0", "2"
   */
  @Method()
  async setActiveItemIndex(index: number) {
    this.goToPageWithIndex(index);
  }

  @Listen("scroll", { target: "window" })
  onScroll() {
    if (!this.scrollTimeout) {
      this.scrollTimeout = setTimeout(() => {
        this.setStickyControlPositions();
        this.scrollTimeout = null;
      }, 0);
    }
  }

  @Listen("resize", { target: "window" })
  onResize() {
    if (!this.resizeTimeout) {
      this.resizeTimeout = setTimeout(() => {
        this.viewportHeight = Math.max(document.documentElement.clientHeight || 0, window.innerHeight || 0);
        this.splide.refresh(); // refreshes the slide position after device rotation
        this.resizeTimeout = null;
        if (this.controls) {
          this.calculateStickyControlHorizontalPosition();
          this.manageStickyControlState();
        }
      }, 500);
    }
  }

  componentWillLoad() {
    this.applyScheme();

    this.viewportHeight = Math.max(document.documentElement.clientHeight || 0, window.innerHeight || 0);

    this.slideItems = Array.from(this.hostElement.children).filter.call(
      this.hostElement.children,
      function (child: HTMLElement) {
        return child.tagName === "EON-UI-CAROUSEL-ITEM" && child.getAttribute("slot") === null;
      }
    );

    // if is for editor mode. Editor Mode can be removed as soon as AEM Dialogue is optimized.
    if (this.slideItems.length > 0) {
      this.slideItems.forEach((slide, index) => {
        slide.setAttribute("slot", `slide-${index}`);
      });

      this.initSplideConfig();
    }

    this.initContainerBreakpoints();
  }

  componentDidLoad() {
    this.calculateStickyControlHorizontalPosition();
    // if is for editor mode. Editor Mode can be removed as soon as AEM Dialogue is optimized.
    if (this.slideItems.length > 0) {
      detectSlotChanges(() => {
        this.slideItems = Array.from(this.hostElement.children).filter.call(
          this.hostElement.children,
          function (child: HTMLElement) {
            return child.tagName === "EON-UI-CAROUSEL-ITEM";
          }
        );
        this.slideItems.forEach((slide, index) => {
          slide.setAttribute("slot", `slide-${index}`);
        });

        setTimeout(() => {
          this.reinitSplide();
        });
      }, this.hostElement);

      this.initSplide();
      this.setCssVariables();
      this.waitForLazyLoadedImages(() => {
        this.adjustSlideHeight(/*true*/);
      });
    }
    this.updateCarouselState();
  }

  updateCarouselState() {
    this.carouselState.itemsCount = this.slideItems.length;
    this.carouselState.slidesCount = this.slides && this.slides.length;
    this.carouselState.currentItemIndex = this.activeItemIndex;
    this.carouselState.currentSlideIndex = this.activeSlideIndex;
    this.carouselState.firstVisibleItemIndex = this.activeItemIndex;
    this.carouselState.lastVisibleItemIndex = this.activeItemIndex + this.itemsPerSlide - 1;
  }

  @Watch("scheme")
  applyScheme() {
    applySchemeDelegate(this);
  }

  @Watch("activeScheme")
  setCssVariables(): void {
    setCssVariablesDelegate(this);
  }

  initContainerBreakpoints(): void {
    initContainerBreakpointsDelegate(this.hostElement, this.containerBreakpoints);
  }

  @Watch("itemsPerSlide")
  @Watch("itemsPerSlideDesktop")
  @Watch("itemsPerSlideTablet")
  @Watch("itemsPerSlideMobile")
  @Watch("itemsPerMove")
  @Watch("itemsPerMoveDesktop")
  @Watch("itemsPerMoveTablet")
  @Watch("itemsPerMoveMobile")
  @Watch("slideHeight")
  @Watch("autoplay")
  @Watch("loop")
  @Watch("pauseOnHover")
  @Watch("autoWidth")
  private initSplideConfig() {
    const defaultPerPage = this.itemsPerSlide ? this.itemsPerSlide : 1;
    const defaultPerMove = this.itemsPerMove ? this.itemsPerMove : 1;

    this.splideConfig = {
      perPage: defaultPerPage,
      perMove: defaultPerMove,
      height: this.slideHeight ? this.slideHeight : "auto",
      autoplay: this.autoplay ? true : false,
      interval: this.autoplay ? this.autoplay : 5,
      rewind: this.loop ? true : false,
      pauseOnHover: this.pauseOnHover ? true : false,
      pauseOnFocus: this.pauseOnHover ? true : false,
      autoWidth: this.autoWidth ? true : false,
      keyboard: "global",
      paginationKeyboard: true,
      breakpoints: {
        1535: {
          gap: this.gap ? this.gap : "40px",
          ...(this.itemsPerSlideDesktop ? { perPage: this.itemsPerSlideDesktop } : { perPage: defaultPerPage }),
          ...(this.itemsPerMoveDesktop ? { perMove: this.itemsPerMoveDesktop } : { perMove: defaultPerMove })
        },
        1023: {
          gap: this.gap ? this.gap : "24px",
          ...(this.itemsPerSlideTablet ? { perPage: this.itemsPerSlideTablet } : { perPage: defaultPerPage }),
          ...(this.itemsPerMoveTablet ? { perMove: this.itemsPerMoveTablet } : { perMove: defaultPerMove })
        },
        767: {
          gap: this.gap ? this.gap : "16px",
          ...(this.itemsPerSlideMobile ? { perPage: this.itemsPerSlideMobile } : { perPage: defaultPerPage }),
          ...(this.itemsPerMoveMobile ? { perMove: this.itemsPerMoveMobile } : { perMove: defaultPerMove })
        }
      }
    };

    if (this.splide) {
      this.reinitSplide();
    }
  }

  private reinitSplide() {
    // if is for editor mode. Editor Mode can be removed as soon as AEM Dialogue is optimized.
    if (this.slideItems.length > 0) {
      this.splide.destroy();
      this.initSplide();
    }
  }

  private initSplide() {
    this.splide = new Splide(this.splideContainer, {
      //easing: "cubic-bezier(0.45, 0, 0.55, 1)",
      speed: 700,
      arrows: false,
      waitForTransition: false,
      gap: this.gap ? this.gap : "48px",
      ...this.splideConfig
    });

    this.splide.on("mounted", () => {
      var items = this.splide.length; //Amount of items
      var perPage = this.splide.options.perPage; //The perPage value

      if (items <= perPage) {
        //If amount of items is equal or less than perPage value
        this.splide.options = {
          pagination: false
        };
      }
      // data.items contains all dot items
      this.slides = this.splide.Components.Pagination.items;
      this.updateCarouselState();
    });

    this.splide.on("pagination:updated", (_data, _prev) => {
      // data.items contains all dot items
      let index = this.splide.index;
      this.activeItemIndex = index;
      this.activeSlideIndex = this.splide.Components.Pagination.getAt(index)?.page;
      this.focusActiveIndicator();
      this.adjustSlideHeight(/*true*/);
      this.updateCarouselState();
    });

    this.splide.on("moved", () => {
      if (this.controls) {
        this.manageStickyControlState();
      }
    });

    this.splide.on("dragged", () => {
      this.slideDragged.emit(this.carouselState);
    });

    this.splide.on("ready", () => {
      if (this.controls) {
        this.setStickyControlPositions();
        this.manageStickyControlState();
      }
    });

    this.splide.mount();
  }

  private async scrollToNextPage() {
    await this.scrollToCarouselTop();
    this.splide.go(">");
  }

  private async scrollToPreviousPage() {
    await this.scrollToCarouselTop();
    this.splide.go("<");
  }

  private async goToPageWithIndex(index: number) {
    await this.scrollToCarouselTop();
    this.splide.go(`>${index}`);
  }

  private async goToSlideWithIndex(index: number) {
    await this.scrollToCarouselTop();
    this.splide.go(index);
  }

  private focusActiveIndicator() {
    let activeIndicator = this.indicatorContainer.children[this.activeSlideIndex];

    if (activeIndicator) {
      scrollToElement(activeIndicator, {
        scrollContainer: this.indicatorContainer,
        targetPosition: "center",
        direction: "horizontally",
        firstChild: this.indicatorContainer.children[0]
      });
    }
  }

  @Watch("sameHeight")
  @Watch("slideHeight")
  private adjustSlideHeight(/* waitForImages: boolean = false */) {
    // if is for editor mode. Editor Mode can be removed as soon as AEM Dialogue is optimized.
    if (this.slideItems.length > 0) {
      let itemsOnSlide = this.splide.Components.Slides.getIn(this.activeSlideIndex);
      let highestSlideItem = 0;

      itemsOnSlide.forEach((item) => {
        if (!this.sameHeight) item.slide.children[0].style.display = "block";
        if (item.slide.children[0].offsetHeight > highestSlideItem)
          highestSlideItem = item.slide.children[0].offsetHeight;
      });

      this.activeSlideHeight = highestSlideItem;

      if (!this.sameHeight && !this.slideHeight && highestSlideItem)
        this.splideTrack.style.height = highestSlideItem + "px";

      if (this.sameHeight) this.splideTrack.style.height = "auto";
    }
  }

  private async scrollToCarouselTop() {
    if (this.stickyControls) {
      // only scroll to top if sticky controls are active (carousel height > viewport height)
      await scrollToElement(this.hostElement, {
        scrollContainer: window,
        offset: -48,
        speed: 1500
      });
    }
  }

  private setStickyControlPositions() {
    this.calculateStickyControlPosition(
      "center",
      "stickyControlCenterPosition",
      this.viewportHeight * 0.5,
      this.viewportHeight * 0.5,
      this.viewportHeight * 0.5
    );
    this.calculateStickyControlPosition("bottom", "stickyControlBottomPosition", this.viewportHeight * 0.5, 16, 0);
  }

  private reinitStickyControlSettings() {
    this.stickyCenterControlPositionFixed = false;
    this.stickyBottomControlPositionFixed = false;
    this.hostElement.style.removeProperty(`--stickyControlBottomPosition`);
    this.hostElement.style.removeProperty(`--stickyControlCenterPosition`);
  }

  private manageStickyControlState() {
    if (this.hostElement.offsetHeight > this.viewportHeight) {
      this.stickyControls = true;
      this.setStickyControlPositions();
    } else {
      this.stickyControls = false;
      this.reinitStickyControlSettings();
    }
  }

  private calculateStickyControlHorizontalPosition() {
    this.stickyControlLeftPosition = this.hostElement.getBoundingClientRect().left;
    this.stickyControlRightPosition = this.hostElement.getBoundingClientRect().right;

    this.hostElement.style.setProperty(`--stickyControlLeftPosition`, `${this.stickyControlLeftPosition}px`);
    this.hostElement.style.setProperty(`--stickyControlRightPosition`, `${this.stickyControlRightPosition}px`);
  }

  @Watch("stickyControls")
  private calculateStickyControlPosition(
    type: "bottom" | "center",
    cssVarName: string,
    stickyControlStartPosition: number,
    stickyControlDuringScrollPosition: number,
    stickyControlEndPosition: number
  ) {
    let carouselHeight = type === "bottom" ? this.hostElement.offsetHeight : this.splideContainer.offsetHeight; // if not botton focus on slide container and ignore bottom navigation
    let windowScrollPosition = window.scrollY;
    let windowScrollPositionBottom = window.scrollY + this.viewportHeight;
    let carouselScrollTop = this.hostElement.getBoundingClientRect().top + windowScrollPosition;
    let stickyControlPosition = "0";
    let isStartPosition =
      windowScrollPositionBottom < carouselScrollTop + stickyControlStartPosition + stickyControlDuringScrollPosition;
    let isEndPosition =
      windowScrollPositionBottom >
      carouselScrollTop + carouselHeight + stickyControlDuringScrollPosition - stickyControlEndPosition;

    type === "bottom"
      ? (this.stickyBottomControlPositionFixed = false)
      : (this.stickyCenterControlPositionFixed = false);

    if (carouselHeight > this.viewportHeight) {
      if (isStartPosition) {
        // Start Position
        stickyControlPosition = `calc(100% - ${Math.round(stickyControlStartPosition)}px)`;
      } else if (isEndPosition) {
        // End Position
        stickyControlPosition = `${stickyControlEndPosition}px`;
      } else {
        // During Scroll Position
        type === "bottom"
          ? (this.stickyBottomControlPositionFixed = true)
          : (this.stickyCenterControlPositionFixed = true);
        stickyControlPosition = `${stickyControlDuringScrollPosition}px`;
      }
    } else {
      this.hostElement.style.removeProperty(`--${cssVarName}`);
      return;
    }

    this.hostElement.style.setProperty(`--${cssVarName}`, stickyControlPosition);
  }

  private waitForLazyLoadedImages(callback: () => any): void {
    const firstEonUiImage = this.slideItems[0]?.getElementsByTagName("EON-UI-IMAGE")[0];
    const firstHtmlImg = this.slideItems[0]?.getElementsByTagName("IMG")[0] as HTMLImageElement;

    // Check for eon-ui-image
    if (firstEonUiImage) {
      firstEonUiImage.addEventListener("imageSrcLoaded", () => {
        callback();
      });
    }

    // Check for html img
    if (firstHtmlImg) {
      firstHtmlImg.onload = callback();
    }

    // no image
    if (!firstEonUiImage && !firstHtmlImg) {
      callback();
    }
  }

  private handleControlPrevKeyDown(e: KeyboardEvent) {
    switch (e.key) {
      case KeyIds.SPACE:
        e.preventDefault();
        this.scrollToPreviousPage();
        break;
      case KeyIds.ENTER:
        e.preventDefault();
        this.scrollToPreviousPage();
        break;
      default:
        break;
    }
  }

  private handleControlNextKeyDown(e: KeyboardEvent) {
    switch (e.key) {
      case KeyIds.SPACE:
        e.preventDefault();
        this.scrollToNextPage();
        break;
      case KeyIds.ENTER:
        e.preventDefault();
        this.scrollToNextPage();
        break;
      default:
        break;
    }
  }

  private handleIndicatorKeyDown(e: KeyboardEvent, index) {
    switch (e.key) {
      case KeyIds.SPACE:
        e.preventDefault();
        this.goToPageWithIndex(index);
        break;
      case KeyIds.ENTER:
        e.preventDefault();
        this.goToPageWithIndex(index);
        break;
      default:
        break;
    }
  }

  @Watch("slides")
  renderIndicators() {
    return (
      <div
        ref={(el) => (this.indicatorContainer = el as HTMLInputElement)}
        class={pfx({
          ["small"]: this.indicatorSize === INDICATORSIZE_SMALL,
          ["indicators-inner"]: true
        })}
      >
        {this.slides &&
          this.slides.map((_indicator, index) => (
            /* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */
            <div
              class={pfx({
                ["indicator"]: true,
                ["act"]: this.activeSlideIndex === index,
                ["small"]: this.indicatorSize === INDICATORSIZE_SMALL
              })}
              role="button"
              onClick={() => this.goToPageWithIndex(index)}
              onKeyDown={(event: KeyboardEvent) => this.handleIndicatorKeyDown(event, index)}
              tabindex="0"
            ></div>
          ))}
      </div>
    );
  }

  renderControlPrev(additionalClass?: string) {
    return (
      <div
        class={pfx({
          ["control prev"]: true,
          ["dark"]: this.darkenControls,
          [`control-style-${this.controlStyle}`]: !!this.controlStyle,
          [`${additionalClass}`]: !!additionalClass
        })}
        role="button"
        tabIndex={this.arrowFocus ? 0 : -1}
        onClick={() => this.scrollToPreviousPage()}
        onKeyDown={(event: KeyboardEvent) => this.handleControlPrevKeyDown(event)}
      >
        <div class={pfx("icon-carrier")}>
          <div class={pfx("carrier")}></div>
          <eon-ui-icon
            aria-label={this.chevronLeftIcon}
            class={pfx("icon")}
            name={this.chevronLeftIcon}
            size={ICONSIZE_NORMAL}
            alignment-x="center"
            alignment-y="center"
          ></eon-ui-icon>
        </div>
      </div>
    );
  }

  renderControlNext(additionalClass?: string) {
    return (
      <div
        class={pfx({
          ["control next"]: true,
          ["dark"]: this.darkenControls,
          [`control-style-${this.controlStyle}`]: !!this.controlStyle,
          [`${additionalClass}`]: !!additionalClass
        })}
        role="button"
        tabIndex={this.arrowFocus ? 0 : -1}
        onClick={() => this.scrollToNextPage()}
        onKeyDown={(event: KeyboardEvent) => this.handleControlNextKeyDown(event)}
      >
        <div class={pfx("icon-carrier")}>
          <div class={pfx("carrier")}></div>
          <eon-ui-icon
            aria-label={this.chevronRightIcon}
            class={pfx("icon")}
            name={this.chevronRightIcon}
            size={ICONSIZE_NORMAL}
            alignment-x="center"
            alignment-y="center"
          ></eon-ui-icon>
        </div>
      </div>
    );
  }

  render() {
    return (
      <Host>
        {this.slideItems.length > 0 ? (
          <div
            class={pfx({
              ["carousel"]: true,
              [`control-style-${this.controlStyle}`]: !!this.controlStyle,
              [`controls`]: this.controls,
              [`full-width`]: !!this.fullWidth
            })}
          >
            <div
              class={pfx({
                ["slides"]: true,
                ["same-height"]: !!this.sameHeight
              })}
            >
              {/* CONTROL: PREVIOUS */}
              <div
                class={pfx({
                  ["control-wrapper"]: true,
                  ["prev"]: true,
                  ["visible"]: this.controls && this.slides?.length > 0 && (this.activeItemIndex > 0 || this.loop),
                  ["fixed"]: this.stickyCenterControlPositionFixed
                })}
              >
                {this.renderControlPrev()}
              </div>
              {/* CONTROL: NEXT */}
              <div
                class={pfx({
                  ["control-wrapper"]: true,
                  ["next"]: true,
                  ["visible"]:
                    this.controls &&
                    this.slides?.length > 0 &&
                    (this.activeSlideIndex + 1 < this.slides.length || this.loop),
                  ["fixed"]: this.stickyCenterControlPositionFixed
                })}
              >
                {this.renderControlNext()}
              </div>
              <div
                ref={(el) => (this.splideContainer = el as HTMLInputElement)}
                class={`splide ${pfx("slides-inner")}`}
              >
                <div ref={(el) => (this.splideTrack = el as HTMLInputElement)} class="splide__track">
                  <div role="list" class="splide__list">
                    {this.slideItems.map((slide: any) => {
                      const slideSlot = slide.getAttribute("slot");
                      return (
                        <div class="splide__slide" role="listitem">
                          <slot name={slideSlot}></slot>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
            <div
              class={pfx({
                ["indicators"]: true,
                ["hidden"]: !this.setIndicators,
                ["indicators-inside"]: this.indicatorsOnImage,
                [this.indicatorPosition]: !!this.indicatorPosition
              })}
            >
              {this.renderIndicators()}
            </div>
            <div
              class={pfx({
                ["control-wrapper-mobile-outside"]: true,
                ["fixed"]: this.stickyBottomControlPositionFixed,
                ["visible"]: this.controls
              })}
            >
              {this.renderControlPrev(
                `mobile${this.controls && this.slides && (this.activeItemIndex > 0 || this.loop) ? "" : " inactive"}`
              )}
              {this.renderControlNext(
                `mobile${
                  this.controls && this.slides && (this.activeSlideIndex + 1 < this.slides.length || this.loop)
                    ? ""
                    : " inactive"
                }`
              )}
            </div>
          </div>
        ) : (
          <slot name="editor" />
        )}
      </Host>
    );
  }
}
